import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "page-container"
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = ["textContent"]
const _hoisted_5 = { class: "story-item-type" }
const _hoisted_6 = ["onDrop"]
const _hoisted_7 = ["onDrop"]
const _hoisted_8 = ["onDrop"]

import CurrentLanguage from '@/components/CurrentLanguage.vue';
import StoryForm from '@/components/stories/StoryForm.vue';
import StoryItemForm from '@/components/stories/StoryItemForm.vue';
import { Story } from '@/models/story.model';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { StoriesActions, StoriesState } from '@/stores/stories.store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import {
  TYPE_INFOGRAPHIC,
  TYPE_INFO,
  TYPE_HERO,
  TYPE_QUESTION,
  TYPE_ACTIONS_INITIATIVES,
} from '@/util/constants';
import { StoryItem } from '@/models/story-item.model';
import { useEvents, Event } from '@/services/events.composable';
import { capitalize } from '@/util/string.util';
import { LabelsActions } from '@/stores/labels.store';


export default /*@__PURE__*/_defineComponent({
  __name: 'StoryEdit',
  setup(__props) {

const store = useStore<ShiftCMSState & { ts: StoriesState }>();
const route = useRoute();
const router = useRouter();
const events = useEvents();

const story = computed<Story | null>(() => store.state.ts.story);

const drag = ref<boolean>(false);
const dragItem = ref<StoryItem | null>(null);

const editStory = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryForm),
    data: story.value,
  });
};

const updateLanguage = async () => {
  store.dispatch(StoriesActions.GetStory, route.params.id);
};

onMounted(() => {
  store.dispatch(LabelsActions.GetLabels);
  events.on(Event.LanguageChanged, updateLanguage);
  events.on(Event.DrawerClosed, updateLanguage);
  if (!story.value) {
    store.dispatch(StoriesActions.GetStory, route.params.id);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
  events.off(Event.DrawerClosed, updateLanguage);
});

const addItem = (type: string) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryItemForm),
    data: { story: story.value, type },
  });
};

const editItem = (type: string, item: StoryItem) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(StoryItemForm),
    data: { story: story.value, type, value: item },
  });
};

const removeItem = (event: MouseEvent, story: Story, item: StoryItem) => {
  event.stopPropagation();
  store.dispatch(StoriesActions.RemoveStoryItem, { ...item, story });
};

const togglePublished = async () => {
  if (story.value) {
    await store.dispatch(StoriesActions.UpdateStory, {
      ...story.value,
      published: !story.value.published,
    });

    store.dispatch(Actions.ShowNotification, {
      message: story.value.published
        ? 'Story is no longer published'
        : 'Story is published',
      type: story.value.published ? 'info' : 'success',
    });
  }
};

const removeStory = () => {
  if (
    story.value &&
    confirm('Are you absolutely sure you want to delete this story?')
  ) {
    store.dispatch(StoriesActions.DeleteStory, story.value.id);
    router.push('/stories');
  }
};
const getTitle = (item: StoryItem): string => {
  return item.info
    ? item.info.title
    : item.infoGraphic
    ? item.infoGraphic.title
    : item.hero
    ? item.hero.name
    : item.question
    ? item.question.title
    : item.initiatives?.length || item.actions?.length
    ? 'Actions/Initiativis'
    : '';
};

const getDescription = (item: StoryItem): string => {
  return item.info
    ? item.info.description
    : item.infoGraphic
    ? item.infoGraphic.title
    : item.hero
    ? item.hero.name
    : item.question
    ? item.question.title
    : item.initiatives?.length || item.actions?.length
    ? ''
    : '';
};
const getType = (item: StoryItem): string => {
  return item.info
    ? TYPE_INFO
    : item.infoGraphic
    ? TYPE_INFOGRAPHIC
    : item.hero
    ? TYPE_HERO
    : item.question
    ? TYPE_QUESTION
    : item.actions?.length || item.initiatives?.length
    ? TYPE_ACTIONS_INITIATIVES
    : '';
};

const dragStart = (evt: DragEvent, item: StoryItem) => {
  drag.value = true;
  dragItem.value = item;
  evt.dataTransfer?.setData('text', JSON.stringify(item));
};
const dragEnd = (evt: DragEvent) => {
  evt.preventDefault();
  drag.value = false;
  dragItem.value = null;
};

const dragDrop = (evt: DragEvent, column: number, row: number) => {
  evt.preventDefault();
  drag.value = false;
  if (story.value) {
    store.dispatch(StoriesActions.UpdateStoryItemStructure, {
      ...dragItem.value,
      column,
      row,
      story: story.value,
    });
  }
};

const dragOver = (evt: DragEvent) => {
  evt.preventDefault();

  if (evt.target) {
    (evt.target as Element).classList.add('drag-active');
  }
};
const dragLeave = (evt: DragEvent) => {
  evt.preventDefault();
  if (evt.target) {
    (evt.target as Element).classList.remove('drag-active');
  }
};

const getColumns = (items: StoryItem[]): number[] => {
  return items.reduce((acc: number[], item: StoryItem) => {
    return acc.includes(item.column) ? acc : [...acc, item.column].sort();
  }, []);
};
const getColumnItems = (items: StoryItem[], column: number): StoryItem[] => {
  return items
    .filter((item) => item.column === column)
    .sort((a, b) => a.row - b.row);
};

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_table = _resolveComponent("v-table")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_alert = _resolveComponent("v-alert")!

  return (story.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("h2", null, [
          _createTextVNode(_toDisplayString(story.value.title), 1),
          _createVNode(CurrentLanguage)
        ]),
        _createElementVNode("p", null, "This story contains " + _toDisplayString(story.value.items.length) + " items.", 1),
        _createVNode(_component_v_table, { class: "table-container" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_row, { class: "story-container" }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getColumns(story.value.items), (column) => {
                  return (_openBlock(), _createBlock(_component_v_col, { key: column }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getColumnItems(story.value.items, column), (element) => {
                        return (_openBlock(), _createElementBlock("div", {
                          class: "draggable-container",
                          key: element.id
                        }, [
                          _createVNode(_component_v_card, {
                            draggable: "true",
                            onDragstart: (e) => dragStart(e, element),
                            onDragend: _cache[0] || (_cache[0] = (e) => dragEnd(e)),
                            elevation: "2",
                            class: "story-item",
                            onClick: () => editItem(getType(element), element)
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_card_text, null, {
                                default: _withCtx(() => [
                                  _createElementVNode("h4", null, _toDisplayString(getTitle(element)), 1),
                                  (!getTitle(element))
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(getDescription(element)), 1))
                                    : _createCommentVNode("", true),
                                  (getType(element) === _unref(TYPE_ACTIONS_INITIATIVES))
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
                                        _cache[11] || (_cache[11] = _createTextVNode(" Choose from ")),
                                        _createElementVNode("span", {
                                          textContent: _toDisplayString([...element.actions, ...element.initiatives].length)
                                        }, null, 8, _hoisted_4),
                                        _cache[12] || (_cache[12] = _createTextVNode(" choices "))
                                      ]))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("p", _hoisted_5, _toDisplayString(_unref(capitalize)(getType(element))), 1),
                                  _createVNode(_component_v_btn, {
                                    icon: "mdi-delete-outline",
                                    class: "remove-button",
                                    density: "compact",
                                    onClick: _withModifiers(($event: any) => (removeItem($event, story.value, element)), ["prevent"])
                                  }, null, 8, ["onClick"])
                                ]),
                                _: 2
                              }, 1024)
                            ]),
                            _: 2
                          }, 1032, ["onDragstart", "onClick"]),
                          (
                drag.value &&
                dragItem.value &&
                dragItem.value.id !== element.id &&
                element.column === 0
              )
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "drop-target drop-before",
                                onDrop: 
                (e) => dragDrop(e, Math.max(element.column - 1, 0), element.row)
              ,
                                onDragover: _cache[1] || (_cache[1] = (e) => dragOver(e)),
                                onDragleave: _cache[2] || (_cache[2] = (e) => dragLeave(e))
                              }, null, 40, _hoisted_6))
                            : _createCommentVNode("", true),
                          (drag.value && dragItem.value && dragItem.value.id !== element.id)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                class: "drop-target drop-after",
                                onDrop: (e) => dragDrop(e, element.column + 1, element.row),
                                onDragover: _cache[3] || (_cache[3] = (e) => dragOver(e)),
                                onDragleave: _cache[4] || (_cache[4] = (e) => dragLeave(e))
                              }, null, 40, _hoisted_7))
                            : _createCommentVNode("", true),
                          (drag.value && dragItem.value && dragItem.value.id !== element.id)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 2,
                                class: "drop-target drop-bottom",
                                onDrop: (e) => dragDrop(e, element.column, element.row + 1),
                                onDragover: _cache[5] || (_cache[5] = (e) => dragOver(e)),
                                onDragleave: _cache[6] || (_cache[6] = (e) => dragLeave(e))
                              }, null, 40, _hoisted_8))
                            : _createCommentVNode("", true)
                        ]))
                      }), 128))
                    ]),
                    _: 2
                  }, 1024))
                }), 128)),
                _createVNode(_component_v_col)
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_v_btn, { onClick: editStory }, {
          default: _withCtx(() => _cache[13] || (_cache[13] = [
            _createTextVNode("Edit")
          ])),
          _: 1
        }),
        _createVNode(_component_v_menu, null, {
          activator: _withCtx(({ props }) => [
            _createVNode(_component_v_btn, _mergeProps({
              class: "ml-4",
              color: "success"
            }, props), {
              default: _withCtx(() => _cache[14] || (_cache[14] = [
                _createTextVNode("Add Item")
              ])),
              _: 2
            }, 1040)
          ]),
          default: _withCtx(() => [
            _createVNode(_component_v_list, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_list_item, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          onClick: _cache[7] || (_cache[7] = () => addItem(_unref(TYPE_ACTIONS_INITIATIVES))),
                          block: "",
                          variant: "elevated"
                        }, {
                          default: _withCtx(() => _cache[15] || (_cache[15] = [
                            _createTextVNode("Actions/Initiatives")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_list_item, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          onClick: _cache[8] || (_cache[8] = () => addItem(_unref(TYPE_INFO))),
                          block: "",
                          variant: "elevated"
                        }, {
                          default: _withCtx(() => _cache[16] || (_cache[16] = [
                            _createTextVNode("Info Page")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_list_item, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          onClick: _cache[9] || (_cache[9] = () => addItem(_unref(TYPE_INFOGRAPHIC))),
                          block: "",
                          variant: "elevated"
                        }, {
                          default: _withCtx(() => _cache[17] || (_cache[17] = [
                            _createTextVNode("Infographic")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_list_item, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          onClick: _cache[10] || (_cache[10] = () => addItem(_unref(TYPE_QUESTION))),
                          block: "",
                          variant: "elevated"
                        }, {
                          default: _withCtx(() => _cache[18] || (_cache[18] = [
                            _createTextVNode("Quiz question")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _cache[21] || (_cache[21] = _createTextVNode("   ")),
        _createVNode(_component_v_btn, { onClick: togglePublished }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(story.value.published ? 'Un-publish' : 'Publish'), 1)
          ]),
          _: 1
        }),
        _cache[22] || (_cache[22] = _createTextVNode("   ")),
        _createVNode(_component_v_btn, {
          onClick: removeStory,
          color: "warning"
        }, {
          default: _withCtx(() => _cache[19] || (_cache[19] = [
            _createTextVNode("Delete")
          ])),
          _: 1
        }),
        (
        story.value && story.value.items.length && !story.value.items[story.value.items.length - 1].info
      )
          ? (_openBlock(), _createBlock(_component_v_alert, {
              key: 0,
              type: "info",
              class: "mt-8"
            }, {
              default: _withCtx(() => _cache[20] || (_cache[20] = [
                _createTextVNode("Use an info item as a the last page for the story. The info item is used to show the teaser.")
              ])),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})